import { useState } from "react";
import { INITIAL_API_ERROR_POPUP_STATE, INITIAL_INVITE_USER_STATE } from "./constants";

/**
 * Custom hook for handling API operations during account creation
 * Separates API logic from main component logic
 */
export const useAccountCreationAPI = () => {
  const [apiErrorPopupState, setAPIErrorPopupState] = useState(INITIAL_API_ERROR_POPUP_STATE);
  const [inviteUserState, setInviteUserState] = useState(INITIAL_INVITE_USER_STATE);

  /**
   * Sets an API error state for display in popup
   * @param {string} errorMessage - The error message to display
   */
  const setAPIError = (errorMessage) => {
    setAPIErrorPopupState({
      displayPopup: true,
      errorMessage: errorMessage || 'An unexpected error occurred.'
    });
  };

  /**
   * Clears the API error popup state
   */
  const clearAPIError = () => {
    setAPIErrorPopupState(INITIAL_API_ERROR_POPUP_STATE);
  };

  /**
   * Handles API call with standardized error handling
   * @param {Promise} apiPromise - The API promise to handle
   * @param {Function} onSuccess - Callback for successful response
   * @param {string} errorMessage - Custom error message if API fails
   */
  const handleAPICall = async (apiPromise, onSuccess, errorMessage = 'API call failed.') => {
    if (!apiPromise) {
      setAPIError('Unable to process request.');
      return;
    }

    try {
      const result = await apiPromise;
      if (onSuccess) {
        onSuccess(result);
      }
      return result;
    } catch (error) {
      console.error('API call error:', error);
      setAPIError(error?.message || errorMessage);
      throw error;
    }
  };

  /**
   * Calls the InviteUser API with proper error handling
   * @param {Object} params - Parameters for invite user call
   * @param {Function} postInviteUser - The API function to call
   * @param {Function} onSuccess - Success callback
   */
  const callInviteUser = async ({ email, firstName, lastName, returnUrl, postInviteUser, onSuccess }) => {
    if (!email || !firstName || !lastName || !returnUrl) {
      console.error('Missing required information for sending invitation:', { email, firstName, lastName, returnUrl });
      setAPIError('Missing required information for sending invitation.');
      return;
    }

    console.log(`Calling Invite User with: ${email} ${firstName} ${lastName}`);
    
    const postInviteUserPromise = postInviteUser(email, firstName, lastName, returnUrl, inviteUserState, setInviteUserState);
    
    return handleAPICall(
      postInviteUserPromise,
      (inviteState) => {
        if (inviteState?.objData?.success) {
          if (onSuccess) onSuccess();
        } else {
          console.error('Failed to invite user:', inviteState?.objData?.errorMessage);  
          setAPIError(inviteState?.objData?.errorMessage || 'Failed to send invitation email.');
        }
      },
      'An error occurred while sending invitation email.'
    );
  };

  return {
    apiErrorPopupState,
    inviteUserState,
    setAPIError,
    clearAPIError,
    handleAPICall,
    callInviteUser,
    isLoading: inviteUserState.isObjLoading
  };
};